import './ApiDocsPage.css';

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ApiSummary, ApiVersionSummary, listApis, listVersions } from '../api/releases';
import { useAuth } from '../auth/useAuth';
import ChangelogContent from '../components/ChangelogContent';
import GuideContent from '../components/GuideContent';
import MigrationGuideContent from '../components/MigrationGuideContent';
import ScalarViewer from '../components/ScalarViewer';
import { useGuides } from '../query/guides';

/** Human-readable label for a spec variant. */
function specLabel(specId: string): string {
  if (specId === 'v0') {
    return 'v0 — Legacy (Unversioned)';
  }
  if (specId === 'v1') {
    return 'v1 — LRS';
  }
  return specId;
}

interface Props {
  /**
   * Which part of the service's documentation the route addresses: the
   * versioned API reference (specs, changelog, migration guide) or the
   * service's unversioned guides (/apis/:apiId/guides[/:guideId]).
   */
  mode?: 'reference' | 'guides';
}

export default function ApiDocsPage({ mode = 'reference' }: Props) {
  const { role } = useAuth();
  const navigate = useNavigate();
  const params = useParams<{
    apiId?: string;
    versionId?: string;
    section?: string;
    guideId?: string;
  }>();

  const [services, setServices] = useState<ApiSummary[]>([]);
  const [servicesLoading, setServicesLoading] = useState(true);

  useEffect(() => {
    listApis()
      .then(setServices)
      .catch(() => setServices([]))
      .finally(() => setServicesLoading(false));
  }, []);

  const defaultApiId = services[0]?.apiId;
  const apiId = params.apiId ?? defaultApiId ?? '';
  const knownApi = services.some((s) => s.apiId === apiId);

  // The service's guides, if it publishes any. Optional by design: most
  // services answer 404, which the query resolves to an empty list, and the
  // section is simply not drawn. See docs/service-guides.md.
  const guidesQuery = useGuides(knownApi ? apiId : undefined);
  const guides = guidesQuery.data?.guides ?? [];
  const onGuides = mode === 'guides';

  const [versions, setVersions] = useState<ApiVersionSummary[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!apiId || !knownApi) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    listVersions(apiId)
      .then(setVersions)
      .catch(() => setError('Could not load API versions.'))
      .finally(() => setLoading(false));
  }, [apiId, knownApi]);

  const canSeePreRelease = role === 'admin' || role === 'developer';
  const visibleVersions = canSeePreRelease
    ? versions
    : versions.filter((v) => v.releaseState === 'released');

  // Newest first in the tree; the API returns oldest → newest.
  const treeVersions = [...visibleVersions].reverse();

  const released = visibleVersions.filter((v) => v.releaseState === 'released');
  const defaultVersion =
    released[released.length - 1] ?? visibleVersions[visibleVersions.length - 1] ?? null;

  // No version is "active" while a guide is open: a guide belongs to the
  // service as deployed now, not to any release in the tree.
  const activeVersion = onGuides
    ? null
    : (params.versionId && visibleVersions.find((v) => v.versionId === params.versionId)) ||
      defaultVersion;

  // Resolve the selected section: a spec variant id, 'changelog' or 'migration'.
  // Fall back to the version's first variant for missing/unknown sections.
  const isDocSection = params.section === 'changelog' || params.section === 'migration';
  const activeVariant = activeVersion
    ? isDocSection
      ? null
      : (activeVersion.specVariants.find((s) => s.specId === params.section) ??
        activeVersion.specVariants[0] ??
        null)
    : null;
  const section = isDocSection ? (params.section as 'changelog' | 'migration') : null;

  // Keep the active version's subtree open, including on deep links.
  const activeVersionId = activeVersion?.versionId;
  useEffect(() => {
    if (activeVersionId) {
      setExpanded((prev) =>
        prev.has(activeVersionId) ? prev : new Set(prev).add(activeVersionId),
      );
    }
  }, [activeVersionId]);

  const select = (versionId: string, sectionId: string) => {
    navigate(`/apis/${apiId}/versions/${encodeURIComponent(versionId)}/${sectionId}`);
  };

  const handleVersionClick = (v: ApiVersionSummary) => {
    if (expanded.has(v.versionId)) {
      setExpanded((prev) => {
        const next = new Set(prev);
        next.delete(v.versionId);
        return next;
      });
    } else {
      setExpanded((prev) => new Set(prev).add(v.versionId));
      const firstVariant = v.specVariants[0];
      if (firstVariant) {
        select(v.versionId, firstVariant.specId);
      }
    }
  };

  // Whether the docs (changelog / migration guide) of a version are accessible
  // to the current user. Pre-release docs are gated like pre-release specs.
  const canReadDocs = (v: ApiVersionSummary) => v.releaseState === 'released' || canSeePreRelease;

  // Pre-release specs are gated behind auth — route through the API proxy.
  // Released specs are served directly from CloudFront.
  const swaggerUrl =
    activeVersion && activeVariant
      ? activeVersion.releaseState === 'pre-release'
        ? `/api/v1/swagger/${activeVersion.apiId}/${encodeURIComponent(activeVersion.versionId)}?specId=${activeVariant.specId}`
        : `/swagger/${activeVariant.s3Key}`
      : null;

  if (servicesLoading) {
    return <div className="api-docs-loading">Loading...</div>;
  }
  if (!knownApi) {
    return <div className="api-docs-loading">Unknown API &quot;{apiId}&quot;.</div>;
  }
  if (loading) {
    return <div className="api-docs-loading">Loading...</div>;
  }
  // A guide is readable even when the version list is not; the tree says so.
  if (error && !onGuides) {
    return <div className="api-docs-loading">{error}</div>;
  }

  return (
    <div className="api-docs">
      <div className="api-bar">
        {services.map((api) => (
          <button
            className={`api-tab${api.apiId === apiId ? ' active' : ''}`}
            key={api.apiId}
            onClick={() => navigate(`/apis/${api.apiId}`)}
          >
            {api.displayName}
          </button>
        ))}
      </div>

      <div className="docs-body">
        <aside className="docs-tree">
          {/* The service's own prose, above the releases: it describes the
              service as deployed now and is not a section of any version. Only
              drawn once the service has published at least one guide. */}
          {guides.length > 0 && (
            <div className="tree-guides">
              <Link
                className={`tree-version${onGuides && !params.guideId ? ' active' : ''}`}
                to={`/apis/${apiId}/guides`}
              >
                Guides
              </Link>
              <div className="tree-children">
                {guides.map((guide) => (
                  <Link
                    className={`tree-item${onGuides && params.guideId === guide.id ? ' active' : ''}`}
                    key={guide.id}
                    to={`/apis/${apiId}/guides/${guide.id}`}
                  >
                    {guide.title}
                  </Link>
                ))}
              </div>
              <div className="tree-section-title">API reference</div>
            </div>
          )}

          {error && <p className="tree-empty">{error}</p>}

          {treeVersions.map((v) => {
            const isExpanded = expanded.has(v.versionId);
            const isActiveVersion = activeVersion?.versionId === v.versionId;
            const docsReadable = canReadDocs(v);
            return (
              <div className="tree-root" key={v.versionId}>
                <button
                  aria-expanded={isExpanded}
                  className={`tree-version${isActiveVersion ? ' active' : ''}`}
                  onClick={() => handleVersionClick(v)}
                >
                  <span aria-hidden={true} className={`chevron${isExpanded ? ' open' : ''}`}>
                    ▸
                  </span>
                  {v.displayName}
                  {v.releaseState === 'pre-release' && (
                    <span className="pre-release-badge">pre</span>
                  )}
                </button>
                {isExpanded && (
                  <div className="tree-children">
                    {v.specVariants.map((variant) => (
                      <button
                        className={`tree-item${
                          isActiveVersion && activeVariant?.specId === variant.specId
                            ? ' active'
                            : ''
                        }`}
                        key={variant.specId}
                        onClick={() => select(v.versionId, variant.specId)}
                      >
                        {specLabel(variant.specId)}
                      </button>
                    ))}
                    <button
                      className={`tree-item${isActiveVersion && section === 'changelog' ? ' active' : ''}`}
                      disabled={!v.changelogS3Key || !docsReadable}
                      title={
                        !docsReadable
                          ? 'You do not have permission to view this changelog.'
                          : !v.changelogS3Key
                            ? 'No changelog has been generated for this version.'
                            : undefined
                      }
                      onClick={() => select(v.versionId, 'changelog')}
                    >
                      Changelog
                    </button>
                    <button
                      className={`tree-item${isActiveVersion && section === 'migration' ? ' active' : ''}`}
                      disabled={!v.migrationGuideS3Key || !docsReadable}
                      title={
                        !docsReadable
                          ? 'You do not have permission to view this migration guide.'
                          : !v.migrationGuideS3Key
                            ? 'No migration guide is available for this version.'
                            : undefined
                      }
                      onClick={() => select(v.versionId, 'migration')}
                    >
                      Migration Guide
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          {treeVersions.length === 0 && !error && (
            <p className="tree-empty">No versions published.</p>
          )}

          {/* Outside the version list on purpose: this service's error pages
              are not a section of any one release. The link is here only so a
              reader browsing the reference can find them. */}
          {apiId && (
            <Link className="tree-aside-link" to={`/errors/${apiId}`}>
              Error catalogue →
            </Link>
          )}
        </aside>

        <div className="docs-content">
          {onGuides && <GuideContent apiId={apiId} guideId={params.guideId} />}
          {activeVersion && section === 'changelog' && (
            <ChangelogContent apiId={apiId} versionId={activeVersion.versionId} />
          )}
          {activeVersion && section === 'migration' && (
            <MigrationGuideContent
              s3Key={activeVersion.migrationGuideS3Key}
              versionId={activeVersion.versionId}
            />
          )}
          {!onGuides &&
            !section &&
            (swaggerUrl ? (
              <ScalarViewer key={swaggerUrl} specUrl={swaggerUrl} />
            ) : (
              <div className="no-spec">
                <p>No API versions published yet.</p>
                {canSeePreRelease && (
                  <Link to="/admin">Upload a spec in the admin interface →</Link>
                )}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
